import {
  afterNextRender,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { EnumStatusDocumento } from '../../../core/enum/EnumStatusDocumento';
import { Doc } from '../../../core/models/documento';
import { centralizarModalNaAreaVisivel, VivestModalElement } from '../../utils/modal-iframe';

/** Evento emitido ao incluir ou remover o arquivo de um documento; `file` nulo indica remoção. */
export interface ArquivoDocumento {
  tipoDocumento: string;
  file: File | null;
}

const EXTENSOES_VALIDAS = ['jpg', 'jpeg', 'png', 'pdf'];
const TEXTO_ADICIONAR = 'Toque aqui para adicionar';
/** Tamanho máximo do arquivo, em MB, quando o componente pai não informa. */
const TAMANHO_MAXIMO_PADRAO_MB = 10;
const BYTES_POR_MB = 1024 * 1024;

/** Deixa a extensão em minúsculo (ex.: FOTO.JPG → FOTO.jpg), mantendo o restante do nome. */
function normalizarNomeArquivo(nome: string): string {
  const indicePonto = nome.lastIndexOf('.');
  return indicePonto === -1 ? nome : nome.slice(0, indicePonto) + nome.slice(indicePonto).toLowerCase();
}

function extensaoValida(nome: string): boolean {
  const extensao = nome.split('.').pop()?.toLowerCase() ?? '';
  return EXTENSOES_VALIDAS.includes(extensao);
}

/**
 * Card de um documento com o modal de upload.
 * - Sem envio ou com erro: abre o modal para incluir o arquivo.
 * - Em análise ou concluído (`processed`): mostra o arquivo enviado e o status, sem permitir novo upload.
 */
@Component({
  selector: 'app-upload-doc-modal',
  styleUrl: './upload-doc-modal.scss',
  templateUrl: './upload-doc-modal.html',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class UploadDocModal {
  readonly documento = input.required<Doc>();
  /** Documento já enviado e travado (em análise ou concluído, conforme `documento.idStatus`). */
  readonly processed = input(false);
  /** Documento enviado que voltou com erro e precisa ser reenviado. */
  readonly withError = input(false);

  /**
   * Centraliza o modal na parte do iframe que a pessoa está vendo (fluxo do requerimento). Desligado,
   * o modal fica na posição padrão da lib.
   */
  readonly centralizarNaAreaVisivel = input(false);

  /** Tamanho máximo do arquivo, em MB. Sem valor (ou inválido), usa 10 MB. */
  readonly tamanhoMaximo = input<number | null | undefined>(TAMANHO_MAXIMO_PADRAO_MB);

  /** Mostra a lixeira para remover o arquivo já enviado; quem usa o card decide quando a remoção é permitida. */
  readonly removivel = input(false);

  readonly saveDoc = output<ArquivoDocumento>();
  /** Clique na lixeira; emite o `tipoDocumento`. */
  readonly removeDoc = output<string>();

  private readonly injector = inject(Injector);
  private readonly fileInput = viewChild<ElementRef<HTMLInputElement>>('fileInput');
  private readonly modal = viewChild<ElementRef<VivestModalElement>>('modal');

  readonly openModal = signal(false);
  /** Arquivo confirmado no card (após clicar em Continuar no modal). */
  readonly arquivoIncluido = signal<File | null>(null);
  /** Arquivo escolhido dentro do modal, ainda não confirmado. */
  readonly arquivoSelecionado = signal<File | null>(null);
  /** Motivo de o último arquivo escolhido ter sido recusado; vazio quando não há erro. */
  readonly erroArquivo = signal('');

  readonly tamanhoMaximoMb = computed(() => {
    const tamanho = this.tamanhoMaximo();
    return tamanho && tamanho > 0 ? tamanho : TAMANHO_MAXIMO_PADRAO_MB;
  });

  readonly analyzing = computed(() => this.processed() && this.documento().idStatus === EnumStatusDocumento.EmAnalise);
  readonly concluido = computed(() => this.processed() && !this.analyzing());
  /** Recusado: o arquivo incluído foi recusado e precisa ser trocado. */
  readonly recusado = computed(() => !this.processed() && !!this.documento().motivoRecusa);
  /** Arquivo incluído no card ou, liberado para troca, o já enviado (`documento.nomeArquivo`). */
  readonly addFile = computed(
    () => (this.arquivoIncluido() !== null || !!this.documento().nomeArquivo) && !this.recusado(),
  );

  /** Status exibido à direita do card; vazio quando o documento não está travado. */
  readonly txtStatus = computed(() => {
    if (this.analyzing()) return 'Em análise';
    if (this.concluido()) return 'Concluído';
    return '';
  });

  /** Texto abaixo do nome do documento: arquivo enviado, motivo da recusa, arquivo incluído ou convite para adicionar. */
  readonly txtDownload = computed(() => {
    if (this.processed()) return this.documento().nomeArquivo ?? '';
    if (this.recusado()) return this.documento().motivoRecusa ?? '';
    const incluido = this.arquivoIncluido();
    if (incluido) return normalizarNomeArquivo(incluido.name);
    return this.documento().nomeArquivo || TEXTO_ADICIONAR;
  });

  readonly nomeArquivoSelecionado = computed(() => {
    const selecionado = this.arquivoSelecionado();
    return selecionado ? normalizarNomeArquivo(selecionado.name) : '';
  });

  constructor() {
    // Ligado, o modal aparece no meio da área que a pessoa está vendo, e não no meio da página inteira.
    effect((onCleanup) => {
      if (!this.openModal() || !this.centralizarNaAreaVisivel()) return;

      let parar = (): void => {};
      // Depois da renderização, quando o vivest-modal já recebeu o `open`.
      const render = afterNextRender(
        () => {
          const elemento = this.modal()?.nativeElement;
          if (elemento) parar = centralizarModalNaAreaVisivel(elemento);
        },
        { injector: this.injector },
      );

      onCleanup(() => {
        render.destroy();
        parar();
      });
    });
  }

  /**
   * Abre o modal partindo do arquivo já incluído, para que Cancelar não o descarte. Recusado, abre sem
   * arquivo para a pessoa escolher outro.
   */
  openUploadModal(): void {
    if (this.processed()) return;
    this.arquivoSelecionado.set(this.recusado() ? null : this.arquivoIncluido());
    this.erroArquivo.set('');
    this.openModal.set(true);
  }

  closeModal(): void {
    this.openModal.set(false);
    this.limparInput();
  }

  onFileSelected(event: Event): void {
    const arquivo = (event.target as HTMLInputElement).files?.[0];
    if (!arquivo) return;

    if (!extensaoValida(arquivo.name)) {
      this.erroArquivo.set('');
      this.limparInput();
      return;
    }

    if (arquivo.size > this.tamanhoMaximoMb() * BYTES_POR_MB) {
      this.erroArquivo.set(`O arquivo deve ter até ${this.tamanhoMaximoMb()} MB. Escolha outro arquivo.`);
      this.limparInput();
      return;
    }

    this.erroArquivo.set('');
    this.arquivoSelecionado.set(arquivo);
  }

  /** Remove o arquivo escolhido no modal e o já incluído no card. */
  removeFile(event?: Event): void {
    // O "Remover" fica dentro do <label> do input; sem isso o clique reabriria o seletor de arquivos.
    event?.preventDefault();
    event?.stopPropagation();

    this.arquivoSelecionado.set(null);
    this.limparInput();

    if (this.arquivoIncluido()) {
      this.arquivoIncluido.set(null);
      this.saveDoc.emit({ tipoDocumento: this.documento().tipoDocumento, file: null });
    }
  }

  /**
   * Pede ao componente pai a remoção do arquivo enviado. Esquece o arquivo incluído para o card, ao voltar a pendente,
   * não mostrar o check verde do arquivo removido; enquanto há envio o card exibe o nome vindo de `documento`.
   */
  removerEnviado(event: Event): void {
    // A lixeira fica dentro do card; sem isso o clique também abriria o modal de upload.
    event.preventDefault();
    event.stopPropagation();

    this.arquivoIncluido.set(null);
    this.removeDoc.emit(this.documento().tipoDocumento);
  }

  onSubmit(): void {
    const arquivo = this.arquivoSelecionado();
    if (!arquivo) return;

    this.arquivoIncluido.set(arquivo);
    this.saveDoc.emit({ tipoDocumento: this.documento().tipoDocumento, file: arquivo });
    this.closeModal();
  }

  private limparInput(): void {
    const input = this.fileInput()?.nativeElement;
    if (input) input.value = '';
  }
}
